import { useEffect, useMemo, useState } from 'react';
import {
  IconAlertCircle,
  IconChecks,
  IconCircleCheck,
  IconDownload,
  IconEdit,
  IconFileText,
  IconRefresh,
  IconScale,
  IconSparkles,
  IconTrash,
} from '@tabler/icons-react';
import Btn from './ui/Btn';
import DictationButton from './DictationButton';
import SendExportToDriveBtn from './SendExportToDriveBtn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import type {
  Conclusion,
  ConclusionCitation,
  ConclusionSection,
  ConclusionStatus,
  ConclusionType,
} from '../lib/types';

/**
 * Onglet « Conclusions » du dossier (ADR-027).
 *
 * Liste les conclusions générées, permet d'en créer une nouvelle (modal
 * type + hint), éditer les sections markdown, basculer le status,
 * supprimer. Citations corpus juridique pénal en bas de chaque détail.
 */

const TYPE_LABELS: Record<ConclusionType, string> = {
  cc_reglement: 'Chambre du conseil — règlement',
  cmi_recours: 'CMI — recours',
  fond_correctionnel: 'Fond correctionnel',
  appel_correctionnel: 'Appel correctionnel',
  fond_assises: 'Fond assises',
  cassation: 'Cassation',
  autre: 'Autre',
};

const STATUS_LABELS: Record<ConclusionStatus, string> = {
  draft: 'Brouillon',
  finalized: 'Finalisé',
  archived: 'Archivé',
};

const STATUS_BADGE: Record<ConclusionStatus, string> = {
  draft: 'bg-cat-imp-pastel text-cat-imp-text',
  finalized: 'bg-cat-fav-pastel text-cat-fav',
  archived: 'bg-paper-subtle text-text-tertiary',
};

const SOURCE_TYPE_LABELS: Record<ConclusionCitation['source_type'], string> = {
  doctrine: 'Doctrine',
  jurisprudence_cass: 'Jurisprudence Cass.',
  code_legal: 'Texte légal',
};

type Props = {
  dossierId: string;
};

export default function ConclusionsTab({ dossierId }: Props) {
  const [items, setItems] = useState<Conclusion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = async () => {
    try {
      const data = await api.get<Conclusion[]>(`/dossiers/${dossierId}/conclusions`);
      setItems(data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  const selected = useMemo(
    () => items.find((c) => c.id === selectedId) ?? null,
    [items, selectedId]
  );

  const pollTaskStatus = (taskId: string, conclusionTypeLabel: string) => {
    // Polling toutes les 4s — survit aux longues générations Opus
    // (1-3 min sur gros contexte). Stop sur SUCCESS / FAILURE.
    const POLL_INTERVAL_MS = 4000;
    const MAX_POLL_DURATION_MS = 360_000; // 6 min hard limit
    const startedAt = Date.now();
    let attempts = 0;

    const intervalId = window.setInterval(async () => {
      attempts += 1;
      if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
        window.clearInterval(intervalId);
        toast.error(
          `Timeout polling task ${taskId} après 6min. Vérifie l'onglet — la conclusion peut être finie.`
        );
        await load();
        return;
      }
      try {
        const status = await api.get<{
          state: string;
          ready: boolean;
          result?: { status?: string; conclusion_id?: string; error?: string };
        }>(`/tasks/${taskId}/status`);
        if (status.ready) {
          window.clearInterval(intervalId);
          if (status.result?.status === 'ok') {
            toast.success(
              `${conclusionTypeLabel} générée (${Math.round((Date.now() - startedAt) / 1000)}s)`
            );
            await load();
            if (status.result.conclusion_id) {
              setSelectedId(status.result.conclusion_id);
            }
          } else {
            toast.error(
              `Génération échouée : ${status.result?.error ?? 'erreur inconnue'}`
            );
            await load();
          }
        } else if (attempts === 3) {
          // À la 3e tentative (~12s) on rappelle gentiment que ça
          // tourne — utile sur les générations longues.
          toast.success(`Génération en cours (état ${status.state})…`);
        }
      } catch (err) {
        // Erreur réseau ponctuelle : on continue le polling
        console.warn('Task status poll failed', err);
      }
    }, POLL_INTERVAL_MS);
  };

  const onGenerate = async (conclusionType: ConclusionType, hint: string) => {
    setGenerating(true);
    try {
      const created = await api.post<
        Conclusion & {
          status?: string;
          async?: boolean;
          message?: string;
          task_id?: string;
        }
      >(`/dossiers/${dossierId}/conclusions/generate`, {
        conclusion_type: conclusionType,
        hint: hint.trim() || null,
      });
      if (
        (created as { async?: boolean }).async ||
        (created as { status?: string }).status === 'queued'
      ) {
        const typeLabel = TYPE_LABELS[conclusionType];
        toast.success(`${typeLabel} : génération lancée…`);
        setGenerateOpen(false);
        const taskId = (created as { task_id?: string }).task_id;
        if (taskId) {
          pollTaskStatus(taskId, typeLabel);
        } else {
          // Fallback legacy si pas de task_id : reload 65s plus tard
          window.setTimeout(() => void load(), 65_000);
        }
      } else {
        setItems((prev) => [created, ...prev]);
        setGenerateOpen(false);
        setSelectedId(created.id);
        toast.success('Conclusion générée');
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur génération');
    } finally {
      setGenerating(false);
    }
  };

  const onDelete = async (conclusion: Conclusion) => {
    if (!confirm(`Supprimer « ${conclusion.title} » ?`)) return;
    try {
      await api.delete(`/conclusions/${conclusion.id}`);
      setItems((prev) => prev.filter((c) => c.id !== conclusion.id));
      if (selectedId === conclusion.id) setSelectedId(null);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur suppression');
    }
  };

  const onPatchStatus = async (conclusion: Conclusion, status: ConclusionStatus) => {
    try {
      const updated = await api.patch<Conclusion>(`/conclusions/${conclusion.id}`, { status });
      setItems((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur mise à jour');
    }
  };

  const onSaveSections = async (conclusion: Conclusion, sections: ConclusionSection[]) => {
    try {
      const updated = await api.patch<Conclusion>(`/conclusions/${conclusion.id}`, { sections });
      setItems((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur enregistrement');
    }
  };

  if (loading) {
    return <div className="px-6 py-5 text-[13px] text-text-secondary">Chargement…</div>;
  }

  return (
    <div className="space-y-5 px-6 py-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-[20px] font-medium text-text-primary">
            Génération (drafts IA)
          </h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            Outil de travail. Génère un draft assisté IA (Opus 4.7 + RAG corpus pénal), à
            retravailler ensuite dans Word avant dépôt formel.
          </p>
        </div>
        <Btn type="button" variant="primary" onClick={() => setGenerateOpen(true)}>
          <IconSparkles size={16} stroke={1.6} />
          <span>Générer une conclusion</span>
        </Btn>
      </header>

      <div className="flex items-start gap-2 rounded-md border border-cat-imp/40 bg-cat-imp-pastel p-3 text-[12.5px] text-cat-imp-text">
        <IconAlertCircle size={16} stroke={1.7} className="mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">Drafts IA — pas des actes juridiques</p>
          <p className="mt-0.5 leading-relaxed">
            Ces conclusions générées sont des brouillons. Pour conserver une conclusion comme acte
            déposé, télécharge le .docx, retravaille dans Word, puis dépose la version finale dans
            l'onglet <strong>Dossier → Nos conclusions</strong>. Aucune copie automatique n'est
            faite entre les deux onglets — c'est volontaire.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-cat-def/40 bg-cat-def-pastel p-3 text-[12.5px] text-cat-def">
          {error}
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
          <aside className="space-y-1.5">
            {items.map((c) => (
              <ConclusionRow
                key={c.id}
                conclusion={c}
                active={c.id === selectedId}
                onSelect={() => setSelectedId(c.id)}
                onDelete={() => void onDelete(c)}
                onStatus={(s) => void onPatchStatus(c, s)}
              />
            ))}
          </aside>
          <div>
            {selected ? (
              <ConclusionDetail
                conclusion={selected}
                dossierId={dossierId}
                onSaveSections={(s) => void onSaveSections(selected, s)}
                onStatus={(s) => void onPatchStatus(selected, s)}
              />
            ) : (
              <div className="rounded-md border border-rule bg-paper-elevated p-6 text-center text-[13px] text-text-tertiary">
                Sélectionne une conclusion à gauche pour la lire ou l'éditer.
              </div>
            )}
          </div>
        </div>
      )}

      {generateOpen && (
        <GenerateModal
          onClose={() => setGenerateOpen(false)}
          onSubmit={onGenerate}
          submitting={generating}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="rounded-md border border-dashed border-rule-strong bg-paper-elevated p-6 text-center">
      <IconScale size={32} stroke={1.4} className="mx-auto text-text-tertiary" />
      <p className="mt-3 text-[14px] font-medium text-text-primary">
        Aucune conclusion encore générée pour ce dossier.
      </p>
      <p className="mt-1 text-[12.5px] text-text-secondary">
        Clique sur « Générer une conclusion » pour produire une première version assistée IA.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row dans la liste
// ---------------------------------------------------------------------------

function ConclusionRow({
  conclusion,
  active,
  onSelect,
  onDelete,
  onStatus,
}: {
  conclusion: Conclusion;
  active: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onStatus: (s: ConclusionStatus) => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group relative w-full text-left rounded-md border p-3 text-[12.5px] transition ${
        active
          ? 'border-accent bg-accent-light'
          : 'border-rule bg-paper-elevated hover:border-rule-strong'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-text-primary truncate">{conclusion.title}</p>
          <p className="mt-0.5 text-[11.5px] text-text-secondary">
            {TYPE_LABELS[conclusion.conclusion_type]}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-pill px-2 py-[1px] font-mono text-[10px] ${STATUS_BADGE[conclusion.status]}`}
        >
          {STATUS_LABELS[conclusion.status]}
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-text-tertiary">
        <span>{new Date(conclusion.created_at).toLocaleDateString('fr-BE')}</span>
        <span className="flex items-center gap-2">
          {conclusion.cost_eur !== null && (
            <span title="Coût IA">{conclusion.cost_eur.toFixed(2)} €</span>
          )}
          {conclusion.corpus_citations.length > 0 && (
            <span title="Citations corpus">
              <IconFileText size={11} stroke={1.6} className="inline" />{' '}
              {conclusion.corpus_citations.length}
            </span>
          )}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        {conclusion.status !== 'finalized' && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onStatus('finalized');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                onStatus('finalized');
              }
            }}
            className="cursor-pointer rounded px-1.5 py-0.5 text-[10px] text-text-secondary hover:bg-paper-subtle hover:text-text-primary"
            title="Marquer finalisé"
          >
            <IconChecks size={12} stroke={1.6} className="inline" /> finaliser
          </span>
        )}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              onDelete();
            }
          }}
          className="cursor-pointer rounded px-1.5 py-0.5 text-[10px] text-text-tertiary hover:bg-paper-subtle hover:text-cat-def"
          title="Supprimer"
        >
          <IconTrash size={12} stroke={1.6} className="inline" />
        </span>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Détail + édition
// ---------------------------------------------------------------------------

function ConclusionDetail({
  conclusion,
  dossierId,
  onSaveSections,
  onStatus,
}: {
  conclusion: Conclusion;
  dossierId: string;
  onSaveSections: (sections: ConclusionSection[]) => void;
  onStatus: (s: ConclusionStatus) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftSections, setDraftSections] = useState<ConclusionSection[]>(conclusion.sections);

  useEffect(() => {
    setDraftSections(conclusion.sections);
    setEditing(false);
  }, [conclusion.id, conclusion.sections]);

  const save = () => {
    onSaveSections(draftSections);
    setEditing(false);
  };

  return (
    <article className="space-y-4 rounded-md border border-rule bg-paper-elevated p-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-text-tertiary">
            {TYPE_LABELS[conclusion.conclusion_type]}
          </p>
          <h2 className="mt-0.5 font-serif text-[18px] font-medium text-text-primary">
            {conclusion.title}
          </h2>
          <p className="mt-0.5 text-[11.5px] text-text-tertiary">
            Généré le {new Date(conclusion.created_at).toLocaleString('fr-BE')}
            {conclusion.model_used && ` · ${conclusion.model_used}`}
            {conclusion.prompt_version && ` · prompt ${conclusion.prompt_version}`}
            {conclusion.cost_eur !== null && ` · ${conclusion.cost_eur.toFixed(2)} €`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={conclusion.status}
            onChange={(e) => onStatus(e.target.value as ConclusionStatus)}
            className="rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[12px]"
          >
            {(['draft', 'finalized', 'archived'] as ConclusionStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <Btn
            type="button"
            variant="primary"
            onClick={() => {
              void api
                .downloadBlob(
                  `/conclusions/${conclusion.id}/export.docx`,
                  `${conclusion.title || 'conclusions'}.docx`
                )
                .catch((e) =>
                  toast.error(e instanceof ApiError ? e.message : 'Téléchargement échoué')
                );
            }}
            title="Télécharger le draft .docx pour retravailler dans Word puis re-déposer dans « Nos conclusions »"
          >
            <IconDownload size={14} stroke={1.6} />
            Télécharger pour retravailler
          </Btn>
          <SendExportToDriveBtn
            dossierId={dossierId}
            exportPath={`/conclusions/${conclusion.id}/export.docx`}
            defaultFilename={`${conclusion.title || 'conclusions'}.docx`}
            defaultSubfolder="PROCEDURE"
          />
          {editing ? (
            <>
              <Btn
                type="button"
                variant="ghost"
                onClick={() => {
                  setDraftSections(conclusion.sections);
                  setEditing(false);
                }}
              >
                Annuler
              </Btn>
              <Btn type="button" variant="primary" onClick={save}>
                <IconCircleCheck size={14} stroke={1.6} />
                Enregistrer
              </Btn>
            </>
          ) : (
            <Btn type="button" variant="secondary" onClick={() => setEditing(true)}>
              <IconEdit size={14} stroke={1.6} />
              Éditer
            </Btn>
          )}
        </div>
      </header>

      {editing ? (
        <div className="space-y-3">
          {draftSections.map((s, i) => (
            <div key={i} className="space-y-1">
              <input
                type="text"
                value={s.heading}
                onChange={(e) => {
                  const next: ConclusionSection[] = draftSections.map((sec, idx) =>
                    idx === i ? { heading: e.target.value, body_markdown: sec.body_markdown } : sec
                  );
                  setDraftSections(next);
                }}
                className="w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[13px] font-medium"
              />
              <div className="relative">
                <textarea
                  value={s.body_markdown}
                  onChange={(e) => {
                    const next: ConclusionSection[] = draftSections.map((sec, idx) =>
                      idx === i ? { heading: sec.heading, body_markdown: e.target.value } : sec
                    );
                    setDraftSections(next);
                  }}
                  rows={Math.max(4, s.body_markdown.split('\n').length + 1)}
                  className="w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 pr-9 font-mono text-[12px]"
                />
                <div className="absolute top-1.5 right-1.5">
                  <DictationButton
                    size="sm"
                    onTranscript={(chunk) => {
                      const next: ConclusionSection[] = draftSections.map((sec, idx) =>
                        idx === i
                          ? {
                              heading: sec.heading,
                              body_markdown:
                                (sec.body_markdown ? sec.body_markdown + ' ' : '') + chunk,
                            }
                          : sec
                      );
                      setDraftSections(next);
                    }}
                    title="Dicter ce paragraphe"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="prose prose-sm max-w-none">
          {conclusion.sections.map((s, i) => (
            <section key={i} className="mb-4">
              <h3 className="font-serif text-[15px] font-medium text-text-primary">{s.heading}</h3>
              <pre className="mt-1 whitespace-pre-wrap break-words font-sans text-[13px] leading-relaxed text-text-primary">
                {s.body_markdown}
              </pre>
            </section>
          ))}
        </div>
      )}

      {conclusion.corpus_citations.length > 0 && (
        <footer className="rounded-md border border-rule bg-paper-subtle p-3 text-[11.5px]">
          <p className="font-medium text-text-primary mb-1.5">
            Citations corpus ({conclusion.corpus_citations.length})
          </p>
          <ul className="space-y-1">
            {conclusion.corpus_citations.map((c) => (
              <li key={c.source_id} className="flex items-start gap-2 text-text-secondary">
                <span className="font-mono text-[10px] text-text-tertiary shrink-0 w-[110px]">
                  {SOURCE_TYPE_LABELS[c.source_type]}
                </span>
                <span className="flex-1 min-w-0">
                  <strong>{c.title}</strong>
                  {c.author && ` — ${c.author}`}
                  {c.year && ` (${c.year})`} · {c.locator}
                  <span className="ml-1 text-text-tertiary">score {c.score.toFixed(2)}</span>
                </span>
              </li>
            ))}
          </ul>
        </footer>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Generate modal
// ---------------------------------------------------------------------------

function GenerateModal({
  onClose,
  onSubmit,
  submitting,
}: {
  onClose: () => void;
  onSubmit: (type: ConclusionType, hint: string) => void;
  submitting: boolean;
}) {
  const [type, setType] = useState<ConclusionType>('cc_reglement');
  const [hint, setHint] = useState('');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-md bg-paper-elevated p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-serif text-[16px] font-medium text-text-primary">
          Générer une conclusion
        </h3>
        <p className="mt-1 text-[12px] text-text-secondary">
          Sonnet 4.6 + RAG sur le corpus juridique pénal indexé.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[12px] text-text-secondary">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ConclusionType)}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            >
              {(Object.keys(TYPE_LABELS) as ConclusionType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[12px] text-text-secondary">
                Indication libre (optionnel)
              </label>
              <DictationButton
                size="sm"
                onTranscript={(chunk) => setHint((prev) => (prev ? prev + ' ' : '') + chunk)}
                title="Dicter l'indication"
              />
            </div>
            <textarea
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              rows={3}
              placeholder="Ex : insister sur la nullité Salduz ; plaider primaire ; demander complément 61quinquies sur la chronologie…"
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            />
            <p className="mt-1 text-[10.5px] text-text-tertiary">
              N'inclus PAS d'identifiants ou de faits précis — uniquement des directions
              d'argumentation. Le contexte factuel du dossier est passé séparément.
            </p>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Btn type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Annuler
          </Btn>
          <Btn
            type="button"
            variant="primary"
            onClick={() => onSubmit(type, hint)}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <IconRefresh size={14} stroke={1.6} className="animate-spin" />
                Génération…
              </>
            ) : (
              <>
                <IconSparkles size={14} stroke={1.6} />
                Lancer
              </>
            )}
          </Btn>
        </div>
      </div>
    </div>
  );
}
